import { useMemo, useState } from "react";
import { evaluateSequence, getSequenceObjectiveStrategy } from "@opus-dt/shared-types";
import { useAutoBatch, useCreateSequenceBatch } from "@/features/simulations/hooks";
import type { SequenceObjective, SequenceRankingRow } from "@/types/sequence.types";

// Dono da lógica do drawer de fila: dispara o lote, acompanha o progresso
// (polling do lote automático — o lote de fila é o mesmo agregado) e traduz os candidatos em
// linhas de ranking com deltas contra a fila ATUAL. A agregação usa o MESMO registry do worker
// (evaluateSequence/estratégias), então o número da tela é o número que ranqueou.

const HOUR_S = 3600;

export function useSequenceBatch(lineId: string) {
  const [batchId, setBatchId] = useState<string | null>(null);
  const [objective, setObjective] = useState<SequenceObjective | null>(null);
  const create = useCreateSequenceBatch(lineId);
  const batch = useAutoBatch(batchId ?? "", 3_000);

  const start = (chosen: SequenceObjective) => {
    setObjective(chosen);
    create.mutate({ objective: chosen }, { onSuccess: (data) => setBatchId(data.id) });
  };

  const reset = () => {
    setBatchId(null);
    setObjective(null);
    create.reset();
  };

  const rows: SequenceRankingRow[] = useMemo(() => {
    const candidates = batch.data?.candidates ?? [];
    if (!objective) return [];
    const strategy = getSequenceObjectiveStrategy(objective);
    const evalOf = (c: (typeof candidates)[number]) =>
      c.sequence ? evaluateSequence(c.sequence.perOrder, c.sequence.totalSetupS) : null;
    const baseline = candidates.find((c) => c.isBaseline);
    const baselineMetric = baseline ? evalOf(baseline) : null;
    const metricOf = (e: ReturnType<typeof evalOf>) =>
      e && e.completed ? (strategy.metric(e).value ?? null) : null;
    const baseValue = metricOf(baselineMetric);

    return candidates.map((c) => {
      const e = evalOf(c);
      const value = metricOf(e);
      return {
        simulationId: c.simulationId,
        label: c.label ?? "Sequência",
        isBaseline: c.isBaseline,
        isWinner: c.isWinner,
        rank: c.rankPosition,
        status: c.status,
        completed: e?.completed ?? false,
        latenessMin: e?.completed ? Math.round(e.totalLatenessS / 60) : null,
        setupMin: e?.completed ? Math.round(e.totalSetupS / 60) : null,
        makespanH:
          e?.completed && Number.isFinite(e.makespanS)
            ? Math.round((e.makespanS / HOUR_S) * 10) / 10
            : null,
        // Delta só existe entre números comparáveis: candidato ou base incompletos ⇒ "—".
        deltaObjective:
          value != null && baseValue != null && !c.isBaseline
            ? Math.round((value - baseValue) * 10) / 10
            : null,
        orders: c.sequence?.perOrder ?? [],
      };
    });
  }, [batch.data, objective]);

  const status = batch.data?.status ?? (batchId ? "QUEUED" : null);
  return {
    objective,
    start,
    reset,
    creating: create.isPending,
    createError: create.error,
    batchId,
    status,
    evaluatedCount: batch.data?.evaluatedCount ?? 0,
    candidateCount: batch.data?.candidateCount ?? 0,
    errorMessage: batch.data?.errorMessage ?? null,
    rows,
    metricLabel: objective ? getSequenceObjectiveStrategy(objective).label : "",
  };
}
